import { InfiniteData } from 'react-query';
import { CheckboxProps } from '@/components/molecules/checkbox/checkbox';
import { RadiobuttonProps } from '@/components/molecules/radiobutton/radiobutton';
import { RangeSliderProps } from '@/components/molecules/range-slider/range-slider';
import {
  CheckBox,
  Filter,
  FilterInput,
  Radio,
  SingleCheckBox,
  Slider,
} from '@/components/organisms/filter-slide-in/filter-slide-in-input-group/filter-slide-in-input-group';
import { ProductCardProps } from '@/components/organisms/product-card/product-card';

/**
 * Formats paginated TravelPortal available-offers API results into `ProductCardProps`
 * so the data can be rendered directly by product card lists on TravelPortal pages.
 *
 * @param searchResult - Infinite query result containing pages with offer items.
 * @param params - Formatting helpers and display mode configuration.
 * @param params.currencyFormatter - Function used to format numeric price values.
 * @param params.pricePerPersonDualChoice - When `true`, uses per-person prices; otherwise total prices.
 * @param params.t - Translation function used for localized labels and text.
 * @returns A flattened array of formatted `ProductCardProps` objects.
 *
 * @remarks
 * - Returns an empty array when no result is provided.
 * - Flattens all pages from the infinite result into a single list.
 * - Keeps TravelPortal-specific fields used by both search and campaign result pages.
 */
export const formatTravelPortalSearchResults = (
  searchResult:
    | InfiniteData<{
        result?: TravelPortal.Components['TravelPortalOffer'][];
      }>
    | undefined,
  {
    currencyFormatter,
    pricePerPersonDualChoice,
    t,
  }: {
    currencyFormatter: (value: number) => string;
    pricePerPersonDualChoice: boolean;
    t: (key: string, params?: any) => string;
  },
): ProductCardProps[] => {
  if (!searchResult) return [];

  return searchResult.pages.flatMap(page =>
    page.result
      ? page.result.map(item => {
          const searchItem = item;

          const images = searchItem.pictureUrls
            ? searchItem.pictureUrls.map(url => ({
                altText: searchItem.hotelName,
                url,
              }))
            : [];

          const bestOfferTotalPrice = pricePerPersonDualChoice
            ? searchItem.totalPricePerPerson
            : searchItem.totalPrice;
          const bestOfferNormalPrice = pricePerPersonDualChoice
            ? searchItem.normalPricePerPerson
            : searchItem.normalPrice;
          const bestOfferDiscountPrice = pricePerPersonDualChoice
            ? searchItem.discountPerPerson
            : searchItem.discount;

          const price = currencyFormatter(bestOfferTotalPrice);
          const offerAmount = currencyFormatter(bestOfferNormalPrice);
          const offerDiscount = currencyFormatter(bestOfferDiscountPrice);

          const adultsLabel =
            searchItem.adultsNo === 1
              ? t('items.common.words.adult')
              : t('items.common.words.adults');
          const childrenLabel =
            searchItem.childrenNo === 1
              ? t('items.common.words.child')
              : t('items.common.words.children');

          const priceLabelAmount =
            searchItem.childrenNo > 0
              ? `${searchItem.adultsNo} ${adultsLabel}, ${searchItem.childrenNo} ${childrenLabel}`
              : `${searchItem.adultsNo} ${adultsLabel}`;

          const priceLabel = pricePerPersonDualChoice
            ? t('items.search.productCard.pricePerPerson', {
                persons: priceLabelAmount,
              })
            : t('items.search.productCard.totalPrice', {
                persons: priceLabelAmount,
              });

          const discount = searchItem.discount !== 0 ? offerDiscount : '0';

          return {
            additionalTag: searchItem.additionalTag,
            departureName: searchItem.departureName,
            description: searchItem.description,
            duration: `${searchItem.daysDuration.toString()} ${t('items.search.productCard.nights')}`,
            homeboundDepartureDate:
              searchItem.homeboundDepartureDate ?? undefined,
            homeboundDepartureName:
              searchItem.homeboundDepartureName ?? undefined,
            hotelName: searchItem.hotelName,
            images,
            includedProducts: searchItem.includedProducts?.map(product => ({
              categoryName: '',
              days: null,
              name: product.name,
              productCategory: 'other' as const,
              productIcon: product.productIcon,
              productId: 0,
            })),
            isSoldOut: false,
            location: searchItem.location,
            offerAmount: discount !== '0' ? offerAmount : '',
            offerId: searchItem.uniqueId,
            offerTag: '',
            outboundArrivalDate: searchItem.outboundArrivalDate ?? undefined,
            outboundDepartureDate:
              searchItem.outboundDepartureDate ?? undefined,
            outboundDepartureName:
              searchItem.outboundDepartureName ?? undefined,
            price,
            priceDetails:
              searchItem.priceDetails as Content.Components['PriceDetailsDto'],
            priceDiscount:
              discount !== '0'
                ? `${t('items.search.productCard.save')} ${discount}`
                : '0',
            priceLabel,
            providerCode: searchItem.brandTag,
            rating: searchItem.stars ?? undefined,
            specialHotelType: searchItem.specialOfferType,
            tags: searchItem.facilities,
            transportMode: searchItem.transportMode,
            uniqueId: searchItem.uniqueId,
            urgencyTags: searchItem.urgencyTags,
            url: searchItem.url,
          };
        })
      : [],
  );
};

const PRICE_RANGE_KEYS = [
  'minTotalPrice',
  'maxTotalPrice',
  'minTotalPricePerPerson',
  'maxTotalPricePerPerson',
];
const FACILITY_RANGE_KEYS = ['minValue', 'maxValue'];

const SLIDER_RANGE_KEYS: Record<string, string[]> = {
  FacilitySliderFilterDto: FACILITY_RANGE_KEYS,
  PriceFilterDto: PRICE_RANGE_KEYS,
  TravelPortalFacilitySliderFilterDto: FACILITY_RANGE_KEYS,
  TravelPortalPriceFilterDto: PRICE_RANGE_KEYS,
};

/**
 * Takes a slider filter's range from the latest search results.
 *
 * The results leave out a slider's range, or the whole filter, when there is
 * no range for them. Merging the results into the previous filters would then
 * keep the previous range, so a range that isn't sent is cleared instead, and
 * the slider isn't rendered.
 *
 * @param filter - The filter to update, any other filter is returned as is.
 * @param latestFilter - The same filter from the latest results, if sent.
 *
 * @returns The filter with the latest range.
 */
export const withLatestSliderRange = <T extends { $type: string }>(
  filter: T,
  latestFilter?: object,
): T => {
  const rangeKeys = SLIDER_RANGE_KEYS[filter.$type];

  if (!rangeKeys) return filter;

  const latestValues = (latestFilter ?? {}) as Record<string, unknown>;

  return {
    ...filter,
    ...Object.fromEntries(
      rangeKeys.map(key => [key, latestValues[key] ?? null]),
    ),
  };
};

// The search parameters that parseFilters can set
export const FILTER_PARAMETER_KEYS = [
  'filterBoardTypes',
  'filterCharter',
  'filterDirect',
  'filterDiscount',
  'filterFacilityIds',
  'filterFacilityValues',
  'filterPricePerPerson',
  'filterProviders',
  'filterStars',
  'filterTransportHotel',
];

/**
 * Checks whether the search is filtered, as the filters in the url are the
 * ones the results were searched with. An empty parameter, e.g. from an older
 * link, isn't a filter.
 *
 * @param searchParams - The search parameters of the page.
 *
 * @returns Whether any filter is set.
 */
export const hasFilterParameters = (
  searchParams: Pick<URLSearchParams, 'get'> | null,
) => FILTER_PARAMETER_KEYS.some(key => !!searchParams?.get(key));

/**
 * Formats the filter data for use in the filter slide-in UI component.
 *
 * This function takes an array of filter DTOs and maps them to a standardized array of filter objects
 * suitable for rendering in the UI. It supports various filter types such as checkboxes, radio buttons,
 * sliders, and dual-choice inputs, and wires up the appropriate change handlers for each filter option.
 *
 * @param filtersData - The array of filter DTOs to format.
 * @param params - An object containing utility functions and configuration:
 * @param params.currencyFormatter - Formats numeric values as currency strings.
 * @param params.handleChangeCheckbox - Handler for checkbox change events.
 * @param params.handleChangeDualChoice - Handler for dual-choice change events.
 * @param params.handleChangeRadioButton - Handler for radio button change events.
 * @param params.handleChangeSlider - Handler for slider change events.
 * @param params.pricePerPersonDualChoice - Indicates if price filters should use per-person pricing.
 * @param params.t - Translation function for localizing labels.
 *
 * @returns An array of formatted filter objects, each with the appropriate type and options for rendering.
 */

export const formatFilterDataForSlideIn = (
  filtersData:
    | Offers.Properties.SearchFilters
    | TravelPortal.Properties.TravelPortalSearchFilters,
  {
    currencyFormatter,
    handleChangeCheckbox,
    handleChangeDualChoice,
    handleChangeRadioButton,
    handleChangeSlider,
    pricePerPersonDualChoice,
    t,
  }: {
    currencyFormatter: (value: number) => string;
    handleChangeCheckbox: (checked: boolean, id: string, index: number) => void;
    handleChangeDualChoice: (id: string) => void;
    handleChangeRadioButton: (
      checked: boolean,
      id: string,
      index: number,
    ) => void;
    handleChangeSlider: (newValue: number, id: string) => void;
    pricePerPersonDualChoice: boolean;
    t: (key: string, params?: any) => string;
  },
): Filter[] => {
  return filtersData
    .map(filter => {
      switch (filter.$type) {
        case 'DiscountFilterDto': {
          return {
            icon: 'Discount',
            id: filter.id.toString(),
            isHighlighted: true,
            label: filter.name,
            name: filter.$type,
            options: [
              {
                checked: filter.selected,
                id: filter.id?.toString() ?? null,
                label: filter.name,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeCheckbox(
                    event.target.checked,
                    filter.id.toString(),
                    0,
                  ),
              },
            ] as CheckboxProps[],
            type: FilterInput.SINGLE_CHECKBOX,
          } as SingleCheckBox;
        }

        case 'PriceFilterDto': {
          const minPrice = pricePerPersonDualChoice
            ? filter.minTotalPricePerPerson
            : filter.minTotalPrice;
          const maxPrice = pricePerPersonDualChoice
            ? filter.maxTotalPricePerPerson
            : filter.maxTotalPrice;

          // Without a price range there is nothing to filter on
          if (minPrice === null || maxPrice === null) return undefined;

          return {
            id: filter.id.toString(),
            label: {
              dualChoiceProps: {
                checked: pricePerPersonDualChoice,
                labelLeft: t('items.common.words.totalPrice'),
                labelRight: t('items.common.words.pricePerPerson'),
                onClick: () => handleChangeDualChoice(filter.id.toString()),
              },
              label: t('items.common.words.price'),
              type: FilterInput.DUAL_CHOICE,
            },
            name: filter.$type,
            options: [
              {
                // Named after the price shown, which the dual choice switches
                ariaLabel: pricePerPersonDualChoice
                  ? t('items.common.words.pricePerPerson')
                  : t('items.common.words.totalPrice'),
                displayValue: true,
                formatValue: value => currencyFormatter(value),
                maxLabel: currencyFormatter(maxPrice),
                maxValue: maxPrice || undefined,
                minLabel: currencyFormatter(minPrice),
                minValue: minPrice || undefined,
                onChange: newValue =>
                  handleChangeSlider(newValue, filter.id.toString()),
                step: filter.stepSize,
                value: maxPrice || undefined,
                valueLabel: t('items.search.filters.upTo'),
              },
            ] as RangeSliderProps[],
            type: FilterInput.SLIDER,
          } as Slider;
        }

        case 'TravelPortalPriceFilterDto': {
          const minPrice = pricePerPersonDualChoice
            ? filter.minTotalPricePerPerson
            : filter.minTotalPrice;
          const maxPrice = pricePerPersonDualChoice
            ? filter.maxTotalPricePerPerson
            : filter.maxTotalPrice;

          // Without a price range there is nothing to filter on
          if (minPrice === null || maxPrice === null) return undefined;

          return {
            id: filter.id.toString(),
            label: {
              dualChoiceProps: {
                checked: pricePerPersonDualChoice,
                labelLeft: t('items.common.words.totalPrice'),
                labelRight: t('items.common.words.pricePerPerson'),
                onClick: () => handleChangeDualChoice(filter.id.toString()),
              },
              label: t('items.common.words.price'),
              type: FilterInput.DUAL_CHOICE,
            },
            name: filter.$type,
            options: [
              {
                // Named after the price shown, which the dual choice switches
                ariaLabel: pricePerPersonDualChoice
                  ? t('items.common.words.pricePerPerson')
                  : t('items.common.words.totalPrice'),
                displayValue: true,
                formatValue: value => currencyFormatter(value),
                maxLabel: currencyFormatter(maxPrice),
                maxValue: maxPrice || undefined,
                minLabel: currencyFormatter(minPrice),
                minValue: minPrice || undefined,
                onChange: newValue =>
                  handleChangeSlider(newValue, filter.id.toString()),
                step: filter.stepSize,
                value: maxPrice || undefined,
                valueLabel: t('items.search.filters.upTo'),
              },
            ] as RangeSliderProps[],
            type: FilterInput.SLIDER,
          } as Slider;
        }

        case 'FacilitySliderFilterDto': {
          // Without a range there is nothing to filter on
          if (filter.minValue === null || filter.maxValue === null) {
            return undefined;
          }

          return {
            facilityId: filter.facility.facilityId?.toString(),
            id: filter.id.toString(),
            label: filter.facility.facilityName,
            name: filter.$type,
            options: [
              {
                ariaLabel: filter.facility.facilityName,
                displayValue: true,
                maxLabel: `${filter.maxValue} ${filter.unit}`,
                maxValue: filter.maxValue || undefined,
                minLabel: `${filter.minValue} ${filter.unit}`,
                minValue: filter.minValue || undefined,
                onChange: newValue =>
                  handleChangeSlider(newValue, filter.id.toString()),
                step: filter.stepSize,
                unit: filter.unit ?? 'm',
                value: filter.maxValue || undefined,
                valueLabel: t('items.search.filters.upTo'),
              },
            ] as RangeSliderProps[],
            type: FilterInput.SLIDER,
          } as Slider;
        }

        case 'FacilityCheckboxFilterDto': {
          const isRadioButton = filter.showAsRadioButtons;

          return isRadioButton
            ? ({
                id: filter.id.toString(),
                label: filter.name,
                name: filter.$type,
                options: filter.facilities?.map((facility, facilityIndex) => ({
                  checked: facility.facilityId === null ? true : false,
                  count: facility.count ?? undefined,
                  disabled: facility.count === 0,
                  id: facility.facilityId?.toString() ?? null,
                  label: facility.facilityName,
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeRadioButton(
                      event.target.checked,
                      filter.id.toString(),
                      facilityIndex,
                    ),
                  value: facility.facilityId?.toString(),
                })) as RadiobuttonProps[],
                type: FilterInput.RADIO,
              } as Radio)
            : ({
                id: filter.id.toString(),
                label: filter.name,
                name: filter.$type,
                options: filter.facilities?.map((facility, facilityIndex) => ({
                  checked: false,
                  count: facility.count ?? undefined,
                  disabled: facility.count === 0,
                  id: facility.facilityId?.toString() ?? null,
                  label: facility.facilityName,
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeCheckbox(
                      event.target.checked,
                      filter.id.toString(),
                      facilityIndex,
                    ),
                  value: facility.facilityId?.toString(),
                })) as CheckboxProps[],
                type: FilterInput.CHECKBOX,
              } as CheckBox);
        }

        case 'TravelPortalFacilitySliderFilterDto': {
          // Without a range there is nothing to filter on
          if (filter.minValue === null || filter.maxValue === null) {
            return undefined;
          }

          return {
            facilityId: filter.facility.facilityId?.toString(),
            id: filter.id.toString(),
            label: filter.facility.facilityName,
            name: filter.$type,
            options: [
              {
                ariaLabel: filter.facility.facilityName,
                displayValue: true,
                maxLabel: `${filter.maxValue} ${filter.unit}`,
                maxValue: filter.maxValue || undefined,
                minLabel: `${filter.minValue} ${filter.unit}`,
                minValue: filter.minValue || undefined,
                onChange: newValue =>
                  handleChangeSlider(newValue, filter.id.toString()),
                step: filter.stepSize,
                unit: filter.unit ?? 'm',
                value: filter.maxValue || undefined,
                valueLabel: t('items.search.filters.upTo'),
              },
            ] as RangeSliderProps[],
            type: FilterInput.SLIDER,
          } as Slider;
        }

        case 'TravelPortalFacilityCheckboxFilterDto': {
          const isRadioButton = filter.showAsRadioButtons;

          return isRadioButton
            ? ({
                id: filter.id.toString(),
                label: filter.name,
                name: filter.$type,
                options: filter.facilities?.map((facility, facilityIndex) => ({
                  checked: facility.facilityId === null ? true : false,
                  count: facility.count ?? undefined,
                  disabled: facility.count === 0,
                  id: facility.facilityId?.toString() ?? null,
                  label: facility.facilityName,
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeRadioButton(
                      event.target.checked,
                      filter.id.toString(),
                      facilityIndex,
                    ),
                  value: facility.facilityId?.toString(),
                })) as RadiobuttonProps[],
                type: FilterInput.RADIO,
              } as Radio)
            : ({
                id: filter.id.toString(),
                label: filter.name,
                name: filter.$type,
                options: filter.facilities?.map((facility, facilityIndex) => ({
                  checked: false,
                  count: facility.count ?? undefined,
                  disabled: facility.count === 0,
                  id: facility.facilityId?.toString() ?? null,
                  label: facility.facilityName,
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeCheckbox(
                      event.target.checked,
                      filter.id.toString(),
                      facilityIndex,
                    ),
                  value: facility.facilityId?.toString(),
                })) as CheckboxProps[],
                type: FilterInput.CHECKBOX,
              } as CheckBox);
        }

        case 'StarFilterDto': {
          return {
            id: filter.id.toString(),
            label: filter.name,
            name: filter.$type,
            // A null count is unknown (Tourpaq); only zero disables a star
            options: filter.selected?.map((_, optionIndex) => ({
              checked: false,
              count: filter.count?.[optionIndex] ?? undefined,
              disabled: filter.count?.[optionIndex] === 0,
              label: `${optionIndex + 1} ${t('items.search.filters.stars')}`,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeCheckbox(
                  event.target.checked,
                  filter.id.toString(),
                  optionIndex,
                ),
              value: `${optionIndex + 1}`,
            })) as CheckboxProps[],
            type: FilterInput.CHECKBOX,
          } as CheckBox;
        }

        case 'TravelPortalStarFilterDto': {
          return {
            id: filter.id.toString(),
            label: filter.name,
            name: filter.$type,
            options: filter.count?.map((option, optionIndex) => ({
              checked: false,
              count: option,
              disabled: option === 0,
              label: `${optionIndex + 1} ${t('items.search.filters.stars')}`,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeCheckbox(
                  event.target.checked,
                  filter.id.toString(),
                  optionIndex,
                ),
              value: `${optionIndex + 1}`,
            })) as CheckboxProps[],
            type: FilterInput.CHECKBOX,
          } as CheckBox;
        }

        case 'TravelPortalProviderFilterDto': {
          return {
            id: filter.id.toString(),
            label: filter.name,
            name: filter.$type,
            options: filter.providers?.map((provider, providerIndex) => ({
              checked: provider.selected,
              count: provider.count,
              disabled: provider.count === 0,
              id: provider.providerCode,
              label: provider.providerName,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeCheckbox(
                  event.target.checked,
                  filter.id.toString(),
                  providerIndex,
                ),
              value: provider.providerCode,
            })) as CheckboxProps[],
            type: FilterInput.CHECKBOX,
          } as CheckBox;
        }

        case 'BoardTypeFilterDto': {
          return {
            id: filter.id.toString(),
            label: filter.name,
            name: filter.$type,
            options: filter.boardTypes?.map((boardType, boardTypeIndex) => ({
              checked: boardType.boardTypeIds === null ? true : false,
              id: boardType.boardTypeIds?.toString() ?? null,
              label: boardType.boardTypeName,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeRadioButton(
                  event.target.checked,
                  filter.id.toString(),
                  boardTypeIndex,
                ),
              value: boardType.boardTypeIds?.toString(),
            })) as RadiobuttonProps[],
            type: FilterInput.RADIO,
          } as Radio;
        }

        case 'DirectFilterDto': {
          return {
            id: filter.id.toString(),
            isHighlighted: false,
            label: filter.name,
            name: filter.$type,
            options: [
              {
                checked: filter.selected,
                id: filter.id?.toString() ?? null,
                label: filter.text,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeCheckbox(
                    event.target.checked,
                    filter.id.toString(),
                    0,
                  ),
              },
            ] as CheckboxProps[],
            type: FilterInput.SINGLE_CHECKBOX,
          } as SingleCheckBox;
        }

        case 'TransportHotelFilterDto': {
          return {
            id: filter.id.toString(),
            isHighlighted: false,
            label: filter.name,
            name: filter.$type,
            options: [
              {
                checked: filter.selected,
                id: filter.id?.toString() ?? null,
                label: filter.text,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeCheckbox(
                    event.target.checked,
                    filter.id.toString(),
                    0,
                  ),
              },
            ] as CheckboxProps[],
            type: FilterInput.SINGLE_CHECKBOX,
          } as SingleCheckBox;
        }

        case 'CharterFilterDto': {
          return {
            id: filter.id.toString(),
            isHighlighted: false,
            label: filter.name,
            name: filter.$type,
            options: [
              {
                checked: filter.selected,
                id: filter.id?.toString() ?? null,
                label: filter.text,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeCheckbox(
                    event.target.checked,
                    filter.id.toString(),
                    0,
                  ),
              },
            ] as CheckboxProps[],
            type: FilterInput.SINGLE_CHECKBOX,
          } as SingleCheckBox;
        }
      }
    })
    .filter((filter): filter is Filter => filter !== undefined);
};

/**
 * Parses an array of filter objects and constructs a parameters object
 * representing the selected filter values for search results.
 *
 * @param filters - An array of filter objects to be parsed.
 * @param pricePerPersonDualChoice - Determines if price per person should be calculated using dual choice logic.
 * @param adultsNo - The number of adults, used for price calculations.
 * @returns An object containing key-value pairs representing the selected filter parameters.
 *
 * @remarks
 * - Handles various filter types including sliders, checkboxes, radio buttons, and single checkboxes.
 * - Special handling for filters such as stars, price, board type, discount, direct, and charter.
 * - Facility-related filters are aggregated and formatted appropriately.
 */

export const parseFilters = (
  filters: Filter[],
  pricePerPersonDualChoice: boolean,
  adultsNo: string,
): Record<string, any> => {
  const parameters: Record<string, any> = {};

  const facilitySliderValues: string[] = [];
  const facilityCheckboxValues: string[] = [];

  filters.forEach(filter => {
    if (
      filter.name === 'StarFilterDto' ||
      filter.name === 'TravelPortalStarFilterDto'
    ) {
      const starValues = (filter.options as CheckboxProps[])
        .filter(option => option.checked)
        .map(option => option.value)
        .join(',');

      if (starValues) {
        parameters.filterStars = starValues;
      }
    } else if (
      (filter.name === 'PriceFilterDto' ||
        filter.name === 'TravelPortalPriceFilterDto') &&
      filter.type === FilterInput.SLIDER
    ) {
      const priceOption = filter.options[0];

      if (priceOption.value !== priceOption.maxValue) {
        const filterValue = pricePerPersonDualChoice
          ? priceOption?.value
          : (priceOption.value as number) / parseInt(adultsNo);

        parameters.filterPricePerPerson = `,${filterValue}`;
      }
    } else if (
      filter.type === FilterInput.CHECKBOX &&
      filter.name === 'TravelPortalProviderFilterDto'
    ) {
      const providerValues = (filter.options as CheckboxProps[])
        .filter(option => option.checked && option.id !== null)
        .map(option => option.value)
        .join(',');

      if (providerValues) {
        parameters.filterProviders = providerValues;
      }
    } else if (
      (filter.type === FilterInput.CHECKBOX ||
        filter.type === FilterInput.RADIO) &&
      filter.name !== 'StarFilterDto' &&
      filter.name !== 'TravelPortalStarFilterDto' &&
      filter.name !== 'TravelPortalProviderFilterDto' &&
      filter.name !== 'BoardTypeFilterDto'
    ) {
      const checkedValues = (filter.options as CheckboxProps[])
        .filter(option => option.checked && option.id !== null)
        .map(option => option.value);

      if (checkedValues.length > 0) {
        facilityCheckboxValues.push(...(checkedValues as string[]));
      }
    } else if (
      filter.type === FilterInput.SLIDER &&
      filter.name !== 'PriceFilterDto' &&
      filter.name !== 'TravelPortalPriceFilterDto'
    ) {
      const sliderValues = filter.options
        .filter(option => option.value !== option.maxValue)
        .map(option => `${filter.facilityId},,${option.value}`);

      if (sliderValues.length > 0) {
        facilitySliderValues.push(...sliderValues);
      }
    } else if (
      filter.type === FilterInput.SINGLE_CHECKBOX &&
      filter.name === 'DiscountFilterDto' &&
      filter.options[0].checked
    ) {
      parameters.filterDiscount = true;
    } else if (
      filter.type === FilterInput.RADIO &&
      filter.name === 'BoardTypeFilterDto'
    ) {
      const boardTypeValues = filter.options
        .filter(option => option.checked && option.id !== null)
        .map(option => option.value);

      if (boardTypeValues.length > 0) {
        parameters.filterBoardTypes = boardTypeValues;
      }
    } else if (
      filter.type === FilterInput.SINGLE_CHECKBOX &&
      filter.name === 'DirectFilterDto' &&
      filter.options[0].checked
    ) {
      parameters.filterDirect = true;
    } else if (
      filter.type === FilterInput.SINGLE_CHECKBOX &&
      filter.name === 'CharterFilterDto' &&
      filter.options[0].checked
    ) {
      parameters.filterCharter = true;
    } else if (
      filter.type === FilterInput.SINGLE_CHECKBOX &&
      filter.name === 'TransportHotelFilterDto' &&
      filter.options[0].checked
    ) {
      parameters.filterTransportHotel = true;
    }
  });

  if (facilityCheckboxValues.length > 0) {
    parameters.filterFacilityIds = facilityCheckboxValues.join(',');
  }

  if (facilitySliderValues.length > 0) {
    parameters.filterFacilityValues = facilitySliderValues.join('|');
  }

  return parameters;
};
